<script setup lang="ts">
/**
 * Каркас экрана: сайдбар 240px и колонка контента. Тулбар рисует сам экран —
 * в макете у него десять контекстов, и общий на всех оказался бы набором
 * условий вместо разметки.
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import CommandPalette from '@/components/CommandPalette.vue'
import MobileTabBar from '@/components/MobileTabBar.vue'
import TrackerSidebar from '@/components/TrackerSidebar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { useAuthStore } from '@/features/auth/store'
import { useProjectsStore } from '@/features/projects/store'

const COLLAPSED_KEY = 'tracker-sidebar-collapsed'

const router = useRouter()

const auth = useAuthStore()
const projects = useProjectsStore()

// Проекты нужны сайдбару на любом экране, а не только в хабе: без них у
// навигации нет ни одного проектного пункта.
onMounted(() => {
  void projects.ensureLoaded()
})

const isMobile = useIsMobile()

const collapsed = ref(localStorage.getItem(COLLAPSED_KEY) === '1')

const user = computed(() =>
  auth.user ? { name: auth.user.name || auth.user.email, role: auth.user.roles[0] } : null,
)

const logout = async () => {
  await auth.logout()
  await router.push({ name: 'login' })
}

const toggleCollapsed = () => {
  collapsed.value = !collapsed.value
  localStorage.setItem(COLLAPSED_KEY, collapsed.value ? '1' : '0')
}
</script>

<template>
  <div class="layout">
    <TrackerSidebar
      v-if="!isMobile"
      :collapsed="collapsed"
      :user="user"
      @toggle-collapse="toggleCollapsed"
      @user-menu="logout"
      @add-project="router.push({ name: 'projects', query: { create: '1' } })"
    />

    <main class="layout__main" :class="{ 'layout__main--mobile': isMobile }">
      <RouterView />
    </main>

    <!-- На телефоне сайдбара нет: навигация уезжает вниз (макет 27:2). -->
    <MobileTabBar v-if="isMobile" />

    <!-- Палитра живёт в каркасе: Cmd+K обязан работать на любом экране. -->
    <CommandPalette />
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  min-height: 100vh;
  background-color: var(--color-bg-app);
}

.layout__main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

/* Место под таб-бар: без отступа последняя строка списка прячется под ним. */
.layout__main--mobile {
  padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px));
}
</style>
